import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";

import klerpLogoPng from "../../assets/fonts/klerp-logo.png";

const NumberFormatter = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatCurrency = (value) => {
    const amount = Number(value) || 0;
    const symbol =
        process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";
    return `${symbol} ${NumberFormatter.format(amount)}`;
};

const formatPaymentGateway = (type) => {
    const labels = {
        cash: "Cash",
        card: "Card",
        onlineTransfer: "Online Bank Transfer",
        creditCard: "Credit Card",
        tng: "Touch 'n Go",
        cheque: "Cheque",
        ecommerce: "E-Commerce",
        others: "Others",
        ziip6: "Ziip 6 Months",
        ziip12: "Ziip 12 Months",
        ziip18: "Ziip 18 Months",
        ziip24: "Ziip 24 Months",
    };
    return labels[type] || type || "Others";
};

const getCreditAmount = (original) => {
    if (original.credit && typeof original.credit === "object") {
        return Number(original.credit.credit) || 0;
    }
    return Number(original.credit) || 0;
};

const getAddressLines = (original) => {
    const addr = original.userId?.address || {};
    const lines = [];

    const lineOne = addr.lineOne || original.lineOne || "";
    const lineTwo = addr.lineTwo || original.lineTwo || "";
    const zip = addr.zip || original.zip || "";
    const city = addr.city || original.city || "";
    const country = addr.country || original.country || "";

    if (lineOne) lines.push(lineOne);
    if (lineTwo) lines.push(lineTwo);

    const cityLine = [zip, city].filter(Boolean).join(", ");
    if (cityLine) lines.push(cityLine);
    if (country) lines.push(country);

    if (lines.length === 0 && original.address) {
        return String(original.address)
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean);
    }

    return lines.length ? lines : ["-"];
};

const getReceiptId = (invoiceId) => {
    const id = String(invoiceId || "");
    const suffix = id.replace(/^KLERP-/i, "");
    return `KLERP-PAID-${suffix}`;
};

const buildTransactions = (original) => {
    const paidAmount =
        Number(original.deposit) ||
        Number(original.invoicePaid) ||
        Number(original.amount) ||
        0;

    return [
        {
            date:
                original.depositDate ||
                original.commenceDate ||
                original.createdAt,
            gateway: formatPaymentGateway(
                original.depositType || original.paymentType
            ),
            referralId: original.invoiceId,
            amount: paidAmount,
        },
    ];
};

const createOfficialReceipt = async (original) => {
    try {
        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([595.28, 841.89]);
        const { width, height } = page.getSize();

        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        const margin = 40;
        const contentWidth = width - margin * 2;
        const tableRight = margin + contentWidth;
        const rowHeight = 22;
        const descColWidth = contentWidth * 0.68;
        const totalColWidth = contentWidth - descColWidth;
        const totalColX = margin + descColWidth;
        const gray = rgb(0.88, 0.88, 0.88);
        const black = rgb(0, 0, 0);
        const white = rgb(1, 1, 1);
        const paidGreen = rgb(0.15, 0.7, 0.2);

        const companyLines = [
            "KLERP TECH SDN. BHD.,",
            "A4-2-7, Solaris Dutamas,",
            "No.1, Jalan Dutamas 1,",
            "50480 Kuala Lumpur",
        ];
        const companyFontSize = 10;
        const companyBlockWidth = Math.max(
            ...companyLines.map((line) =>
                helveticaBold.widthOfTextAtSize(line, companyFontSize)
            )
        );

        const drawPaidStamp = (companyTopY) => {
            const paidLabel = "PAID";
            const paidFontSize = 18;
            const paidTextWidth = helveticaBold.widthOfTextAtSize(
                paidLabel,
                paidFontSize
            );
            const textHeight = helveticaBold.heightAtSize(paidFontSize);
            const padY = 8;
            const boxWidth = companyBlockWidth;
            const boxHeight = textHeight + padY * 2;
            const boxX = tableRight - boxWidth;
            const boxY = companyTopY + 16;
            const textY = boxY + (boxHeight - textHeight) / 2 + paidFontSize * 0.25;

            page.drawRectangle({
                x: boxX,
                y: boxY,
                width: boxWidth,
                height: boxHeight,
                color: paidGreen,
            });

            page.drawText(paidLabel, {
                x: boxX + (boxWidth - paidTextWidth) / 2,
                y: textY,
                size: paidFontSize,
                font: helveticaBold,
                color: white,
            });
        };

        const drawLeftText = (text, x, y, font, size = 10) => {
            page.drawText(text, {
                x,
                y,
                size,
                font,
                color: black,
            });
        };

        const drawRightText = (text, rightX, y, font, size = 10) => {
            const textWidth = font.widthOfTextAtSize(text, size);
            page.drawText(text, {
                x: rightX - textWidth,
                y,
                size,
                font,
                color: black,
            });
        };

        let y = height - margin;

        const logoImageBytes = await fetch(klerpLogoPng).then((res) =>
            res.arrayBuffer()
        );
        const logoImage = await pdfDoc.embedPng(new Uint8Array(logoImageBytes));
        const logoDims = logoImage.scale(0.16);

        const headerContentTop = height - 100;

        drawPaidStamp(headerContentTop);

        page.drawImage(logoImage, {
            x: margin,
            y: headerContentTop - logoDims.height + 8,
            width: logoDims.width,
            height: logoDims.height,
        });

        let companyY = headerContentTop;
        companyLines.forEach((line) => {
            drawRightText(line, tableRight, companyY, helveticaBold, companyFontSize);
            companyY -= 14;
        });

        y = height - 170;

        page.drawRectangle({
            x: margin,
            y: y - 18,
            width: contentWidth,
            height: 22,
            color: gray,
        });

        drawLeftText(getReceiptId(original.invoiceId), margin + 8, y - 12, helveticaBold, 11);

        y -= 34;
        drawLeftText(
            `Invoice Date: ${moment(original.createdAt).format("DD/MM/YYYY")}`,
            margin,
            y,
            helvetica,
            10
        );

        y -= 28;
        drawLeftText(original.userId?.name || "-", margin, y, helveticaBold, 11);

        y -= 16;
        if (original.attention) {
            drawLeftText(`Attn: ${original.attention}`, margin, y, helvetica, 10);
            y -= 14;
        }

        getAddressLines(original).forEach((line) => {
            drawLeftText(line, margin, y, helvetica, 10);
            y -= 14;
        });

        y -= 12;
        const tableTop = y;

        page.drawRectangle({
            x: margin,
            y: tableTop - rowHeight,
            width: contentWidth,
            height: rowHeight,
            color: gray,
        });

        drawLeftText("Description", margin + 8, tableTop - 15, helveticaBold, 10);
        drawRightText("Total", tableRight - 8, tableTop - 15, helveticaBold, 10);

        page.drawLine({
            start: { x: totalColX, y: tableTop },
            end: { x: totalColX, y: tableTop - rowHeight },
            thickness: 0.5,
            color: gray,
        });

        y = tableTop - rowHeight;

        const products = original.products || [];
        let subTotal = 0;

        products.forEach((product) => {
            y -= rowHeight;

            page.drawLine({
                start: { x: margin, y: y + rowHeight },
                end: { x: tableRight, y: y + rowHeight },
                thickness: 0.5,
                color: gray,
            });

            const lineTotal =
                (Number(product.price) || 0) * (Number(product.quantity) || 0);
            subTotal += lineTotal;

            drawLeftText(product.code || "-", margin + 8, y + 7, helvetica, 10);
            drawRightText(
                formatCurrency(lineTotal),
                tableRight - 8,
                y + 7,
                helvetica,
                10
            );
        });

        if (products.length === 0) {
            y -= rowHeight;
            subTotal = Number(original.amount) || 0;
            drawLeftText("Order Items", margin + 8, y + 7, helvetica, 10);
            drawRightText(
                formatCurrency(subTotal),
                tableRight - 8,
                y + 7,
                helvetica,
                10
            );
        }

        const discount = Number(original.discount) || 0;
        const credit = getCreditAmount(original);
        const serviceTax = 0;
        const total = subTotal - discount - credit + serviceTax;

        const summaryRows = [
            { label: "Sub Total", value: subTotal, bold: false },
            { label: "Service Tax", value: serviceTax, bold: false },
            { label: "Credit", value: credit, bold: false },
            { label: "Total", value: total, bold: true },
        ];

        const summaryTop = y;

        summaryRows.forEach((row, index) => {
            y -= rowHeight;

            if (index % 2 === 0) {
                page.drawRectangle({
                    x: totalColX,
                    y,
                    width: totalColWidth,
                    height: rowHeight,
                    color: gray,
                });
            }

            page.drawLine({
                start: { x: margin, y: y + rowHeight },
                end: { x: tableRight, y: y + rowHeight },
                thickness: 0.5,
                color: gray,
            });

            const labelFont = row.bold ? helveticaBold : helvetica;
            drawLeftText(row.label, totalColX + 8, y + 7, labelFont, 10);
            drawRightText(
                formatCurrency(row.value),
                tableRight - 8,
                y + 7,
                labelFont,
                10
            );
        });

        page.drawLine({
            start: { x: totalColX, y: summaryTop },
            end: { x: totalColX, y },
            thickness: 0.5,
            color: gray,
        });

        y -= 30;
        drawLeftText("Transactions", margin, y, helveticaBold, 11);

        y -= 10;
        const transactions = buildTransactions(original);
        const paidTotal = transactions.reduce(
            (sum, txn) => sum + txn.amount,
            0
        );
        const balance = Math.max(0, total - paidTotal);

        const txnColWidths = [
            contentWidth * 0.22,
            contentWidth * 0.28,
            contentWidth * 0.28,
            contentWidth * 0.22,
        ];
        const txnHeaders = [
            "Transaction Date",
            "Gateway",
            "REFERRAL ID",
            "Amount",
        ];

        page.drawRectangle({
            x: margin,
            y: y - rowHeight,
            width: contentWidth,
            height: rowHeight,
            color: gray,
        });

        let txnX = margin;
        txnHeaders.forEach((header, index) => {
            if (index === txnHeaders.length - 1) {
                drawRightText(header, txnX + txnColWidths[index] - 6, y - 15, helveticaBold, 9);
            } else {
                drawLeftText(header, txnX + 6, y - 15, helveticaBold, 9);
            }
            txnX += txnColWidths[index];
        });

        y -= rowHeight;

        transactions.forEach((txn, index) => {
            y -= rowHeight;

            if (index % 2 === 0) {
                page.drawRectangle({
                    x: margin,
                    y,
                    width: contentWidth,
                    height: rowHeight,
                    color: gray,
                });
            }

            const txnValues = [
                moment(txn.date).format("DD/MM/YYYY"),
                txn.gateway,
                txn.referralId,
                formatCurrency(txn.amount),
            ];

            txnX = margin;
            txnValues.forEach((value, colIndex) => {
                if (colIndex === txnValues.length - 1) {
                    drawRightText(
                        String(value),
                        txnX + txnColWidths[colIndex] - 6,
                        y + 7,
                        helvetica,
                        9
                    );
                } else {
                    drawLeftText(
                        String(value),
                        txnX + 6,
                        y + 7,
                        helvetica,
                        9
                    );
                }
                txnX += txnColWidths[colIndex];
            });
        });

        y -= rowHeight;
        page.drawRectangle({
            x: margin,
            y,
            width: contentWidth,
            height: rowHeight,
            color: gray,
        });

        const balanceLabelX =
            margin + txnColWidths[0] + txnColWidths[1] + txnColWidths[2] - 50;
        drawRightText("Balance", balanceLabelX, y + 7, helveticaBold, 9);
        drawRightText(
            formatCurrency(balance),
            tableRight - 8,
            y + 7,
            helveticaBold,
            9
        );

        const pdfBytes = await pdfDoc.save();

        download(
            pdfBytes,
            `Official-Receipt-${original.invoiceId}-${original.userId?.name || "Customer"}`,
            "application/pdf"
        );
    } catch (err) {
        alert("Failed");
        console.log(err);
    }
};

export default createOfficialReceipt;
